import { Component, type ErrorInfo, type ReactNode } from 'react';
import ErrorDisplay from '../../components/ErrorDisplay/ErrorDisplay';

interface Props {
  children: ReactNode;
}

/**
 * Without this, a render error anywhere under PortalContext, its own app door check included, unmounts
 * the whole app and leaves a blank page.
 *
 * The cached `portalMe` profile is dropped because it is the one input that survives a reload. PortalContext
 * renders from it before `me` lands, so a blob this build cannot read would otherwise blank every load until
 * it expires a day later. A checkout older than whatever wrote the blob is the usual way to get one.
 */
class PortalErrorBoundary extends Component<Props, { error: Error | null }> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, _info: ErrorInfo) {
    try {
      localStorage.removeItem('portalMe');
    } catch {
      // storage is blocked, so there is no cache to drop
    }

    // A caught error never reaches the window handlers Sentry listens on, so hand it to them.
    window.reportError?.(error);
  }

  render() {
    return this.state.error ? <ErrorDisplay error={this.state.error} /> : this.props.children;
  }
}

export default PortalErrorBoundary;
